/**
 * Remote parent onboarding API endpoints.
 */

import { apiClient } from './api-client';
import type {
  ParentRecord,
  OnboardingFilters as ParentFilters,
  FullState,
  ProfileDto,
} from '@/types/parent.types';
import type { StudentRecord } from '@/types/remote/student.types';
import type {
  ArchiveEligibility,
  ArchiveRemoteUserDto,
  ArchiveRemoteUserResult,
  ReactivationEligibility,
  ReactivateRemoteUserResult,
  WithPhoneSetupLink,
} from '@/types/user.types';

/** `POST /parents-remote` — create parent + onboarding record. */
export const createProfile = async (
  dto: ProfileDto,
  companyId: string,
): Promise<{ user: unknown; record: unknown }> => {
  const response = await apiClient.post<{ user: unknown; record: unknown }>(
    `/parents-remote?companyId=${companyId}`,
    dto,
  );
  return response.data;
};

/** `PATCH /parents-remote/:userId` */
export const updateProfile = async (
  userId: string,
  dto: ProfileDto,
  companyId: string,
): Promise<WithPhoneSetupLink<unknown>> => {
  const response = await apiClient.patch<WithPhoneSetupLink<unknown>>(
    `/parents-remote/${userId}?companyId=${companyId}`,
    dto,
  );
  return response.data;
};

/** Flatten onboarding filters into query params (`programId` skipped when `programIds` is set). */
function toListParams(
  filters: Omit<ParentFilters, 'companyId'> & { companyId?: string },
  companyId?: string,
): URLSearchParams {
  const params = companyId ? new URLSearchParams({ companyId }) : new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'companyId' || value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
      continue;
    }
    if (key === 'programId' && filters.programIds?.length) continue;
    params.append(key, String(value));
  }
  return params;
}

/** `GET /onboarding/parent-remote` — paginated onboarding list. */
export async function list(
  filters: ParentFilters,
): Promise<{ data: ParentRecord[]; total: number }> {
  const params = toListParams(filters, filters.companyId);
  const { data } = await apiClient.get<{ data: ParentRecord[]; total: number }>(
    `/onboarding/parent-remote?${params}`,
  );
  return data;
}

/** `GET /parents-remote` — scoped list (no companyId required in the path). */
export async function listScoped(
  filters: Omit<ParentFilters, 'companyId'>,
): Promise<{ data: ParentRecord[]; total: number }> {
  const params = toListParams(filters);
  const { data } = await apiClient.get<{ data: ParentRecord[]; total: number }>(
    `/parents-remote?${params}`,
  );
  return data;
}

/** `GET /onboarding/parent-remote/:userId` — record + steps + templates. */
export async function getFullState(userId: string, companyId: string): Promise<FullState> {
  const { data } = await apiClient.get<FullState>(
    `/onboarding/parent-remote/${userId}?companyId=${companyId}`,
  );
  return data;
}

/** `GET /onboarding/parent-remote/:userId/children` */
export async function getChildren(
  parentUserId: string,
  companyId: string,
): Promise<StudentRecord[]> {
  const { data } = await apiClient.get<StudentRecord[]>(
    `/onboarding/parent-remote/${parentUserId}/children?companyId=${companyId}`,
  );
  return data;
}

/** `GET /onboarding/parent-remote/export` — Excel blob. */
export async function exportExcel(
  filters: Omit<ParentFilters, 'companyId' | 'page' | 'pageSize'>,
  companyId: string,
): Promise<Blob> {
  const params = toListParams(filters, companyId);
  const { data } = await apiClient.get<Blob>(`/onboarding/parent-remote/export?${params}`, {
    responseType: 'blob',
  });
  return data;
}

/** `GET /parents-remote/:userId/archive-eligibility` */
export async function getArchiveEligibility(
  userId: string,
  companyId: string,
): Promise<ArchiveEligibility> {
  const { data } = await apiClient.get<ArchiveEligibility>(
    `/parents-remote/${userId}/archive-eligibility?companyId=${companyId}`,
  );
  return data;
}

/** `POST /parents-remote/:userId/archive` */
export async function archiveUser(
  userId: string,
  companyId: string,
  dto: ArchiveRemoteUserDto,
): Promise<ArchiveRemoteUserResult> {
  const { data } = await apiClient.post<ArchiveRemoteUserResult>(
    `/parents-remote/${userId}/archive?companyId=${companyId}`,
    dto,
  );
  return data;
}

/** `GET /parents-remote/:userId/reactivation-eligibility` */
export async function getReactivationEligibility(
  userId: string,
  companyId: string,
): Promise<ReactivationEligibility> {
  const { data } = await apiClient.get<ReactivationEligibility>(
    `/parents-remote/${userId}/reactivation-eligibility?companyId=${companyId}`,
  );
  return data;
}

/** `POST /parents-remote/:userId/reactivate` */
export async function reactivateUser(
  userId: string,
  companyId: string,
): Promise<ReactivateRemoteUserResult> {
  const { data } = await apiClient.post<ReactivateRemoteUserResult>(
    `/parents-remote/${userId}/reactivate?companyId=${companyId}`,
    {},
  );
  return data;
}
